import { ArrowRight, Plus, X } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { apiRequest } from "../api";
import { DataTable } from "../components/DataTable";
import { PageHeader } from "../components/PageHeader";
import { Pagination } from "../components/Pagination";
import type { Session } from "../types";
import type { ShowEngine, ShowPage, ShowStory } from "../show-generator";
import { storyTitle } from "../show-generator";

export function ShowStoriesPage({ session }: { session: Session }) {
  const [stories, setStories] = useState<ShowStory[]>([]);
  const [engines, setEngines] = useState<ShowEngine[]>([]);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [showCreate, setShowCreate] = useState(false);
  const pageSize = 10;

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const [storyPage, enginePage] = await Promise.all([
        apiRequest<ShowPage<ShowStory>>(`/api/show-generator/stories?page=${page}&pageSize=${pageSize}`),
        apiRequest<{ data: ShowEngine[] }>("/api/show-generator/engines"),
      ]);
      setStories(storyPage.data);
      setTotal(storyPage.pagination.total);
      setEngines(enginePage.data);
    } catch (failure) {
      setError(failure instanceof Error ? failure.message : "Could not load stories");
    } finally { setLoading(false); }
  }, [page]);

  useEffect(() => { void load(); }, [load]);

  const rows = stories.map((story) => ({ ...story, displayName: storyTitle(story), progress: progressSummary(story) }));
  return <>
    <PageHeader title="Stories" description="Show Generator productions and current pipeline progress." onRefresh={() => void load()} refreshing={loading} actions={<button className="primary-button" type="button" onClick={() => setShowCreate(true)}><Plus size={16} />Create story</button>} />
    {error ? <div className="error-banner" role="alert">{error}</div> : null}
    <section className="data-surface" aria-busy={loading}>
      {loading && rows.length === 0 ? <div className="loading-state">Loading stories…</div> : <DataTable rows={rows} columns={[{ key: "displayName", label: "Story" }, { key: "id", label: "ID" }, { key: "engine" }, { key: "progress" }, { key: "updatedAt", label: "Updated", kind: "date" }]} rowKey={(row) => String(row.id)} actions={(row) => <Link className="table-action" to={`/show-generator/stories/${String(row.id)}`}>Open<ArrowRight size={15} /></Link>} />}
      <Pagination page={page} pageSize={pageSize} total={total} onPage={setPage} />
    </section>
    {showCreate ? <CreateStoryModal engines={engines} session={session} onClose={() => setShowCreate(false)} /> : null}
  </>;
}

function CreateStoryModal({ engines, session, onClose }: { engines: ShowEngine[]; session: Session; onClose: () => void }) {
  const navigate = useNavigate();
  const [engine, setEngine] = useState(engines[0]?.id ?? "");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  async function create(event: React.FormEvent) {
    event.preventDefault();
    setSubmitting(true);
    setError("");
    try {
      const story = await apiRequest<ShowStory>("/api/show-generator/stories", { method: "POST", body: JSON.stringify({ engine }) }, session.csrfToken);
      navigate(`/show-generator/stories/${story.id}`);
    } catch (failure) {
      setError(failure instanceof Error ? failure.message : "Could not create story");
      setSubmitting(false);
    }
  }
  return <div className="modal-backdrop"><section className="confirm-modal create-story-modal" role="dialog" aria-modal="true" aria-labelledby="create-story-title">
    <header><div><p className="eyebrow">New production</p><h2 id="create-story-title">Create empty story</h2></div><button className="icon-button" type="button" onClick={onClose} title="Close"><X size={18} /></button></header>
    <form onSubmit={(event) => void create(event)}>
      <label><span>Engine</span><select value={engine} onChange={(event) => setEngine(event.target.value)} required>{engines.map((item) => <option key={item.id} value={item.id}>{item.id}</option>)}</select></label>
      <p>The story is created without provider work. Generate its outline from the story workspace.</p>
      {error ? <div className="error-banner" role="alert">{error}</div> : null}
      <footer><button className="secondary-button" type="button" onClick={onClose}>Cancel</button><button className="primary-button" type="submit" disabled={!engine || submitting}>{submitting ? "Creating…" : "Create story"}</button></footer>
    </form>
  </section></div>;
}

function progressSummary(story: ShowStory): string {
  const values = Object.values(story.status.steps);
  const complete = values.filter((value) => value.type === "boolean" ? value.done : value.total > 0 && value.progress >= value.total).length;
  return `${complete}/${values.length} steps complete`;
}
